import { useEffect, useState } from 'react';
import constate from 'constate';

import { AddressHelper } from '../helpers';

const loadStripe = (setStripe) => {
  const stripeJs = document.createElement('script');
  stripeJs.src = 'https://js.stripe.com/v3/';
  stripeJs.async = true;

  stripeJs.onload = () => {
    setStripe(window.Stripe(import.meta.env.VITE_STRIPE_KEY));
  };

  document.body.appendChild(stripeJs);
};

const [StripeStateProvider, useStripeState] = constate(({
  user,
  amount,
  shippingAddress,
  createPayment,
  onPaymentConfirmed,
  currency,
  country,
}) => {
  const [stripe, setStripe] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (stripe) {
      return;
    }

    setStripe('loading');
    loadStripe(setStripe);
  }, []);

  const processPayment = async (
    paymentMethod,
    stripePaymentMethod,
    additionalData,
    event
  ) => {
    setError(null);

    const stripePaymentData = {
      payment_method: stripePaymentMethod,
      shipping: AddressHelper.convertToStripeFormat(shippingAddress),
      receipt_email: user.email,
    };

    const { payment, order } = await createPayment(paymentMethod, additionalData);

    if (!payment) {
      return event && event.complete('fail');
    }

    const paymentConfirmed = await confirmStripePayment(
      payment?.paymentable?.stripe_secret,
      stripePaymentData
    );

    if (!paymentConfirmed) {
      onPaymentConfirmed?.(false, order);

      return event && event.complete('fail');
    }

    event && event.complete('success');

    onPaymentConfirmed?.(true, order);
  };

  const confirmStripePayment = async (stripeSecret, stripePaymentData) => {
    try {
      const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
        stripeSecret,
        stripePaymentData,
        { handleActions: false },
      );

      if (confirmError) {
        throw 'Confirm error.';
      }

      if (paymentIntent.status === 'requires_action') {
        const { error } = await stripe.confirmCardPayment(stripeSecret);

        if (error) {
          throw 'Extra action error.'
        }
      }

      return true;
    } catch (error) {
      setError(error.message);

      return false;
    }
  }

  return {
    stripe,
    processPayment,
    amount,
    currency,
    country,
    shippingAddress,
    error,
  };
});

export { StripeStateProvider, useStripeState };
